"use client";

/**
 * The building blocks every marketing page is made of.
 *
 * These exist so the fourteen pages share a rhythm without becoming fourteen copies of
 * the same page. The rule they encode: a page is a hero, then an argument, then evidence,
 * then a way out. What varies per page is the evidence, which is always a real GrowthSync
 * screen or a real room, never a stock illustration.
 *
 * Deliberately NOT a single <FeaturePage config={...} /> renderer. Five pages generated
 * from one object is exactly how a site ends up feeling like a template: same three cards,
 * same order, same shape, and a reader who has seen one has seen them all.
 */

import type { ReactNode } from "react";
import Link from "next/link";
import { ArrowRight, Check, ChevronDown } from "lucide-react";
import { buttonClasses } from "@/lib/ui/buttonClasses";
import { Shine } from "@/components/ui/shine";
import { useTranslations } from "@/hooks/use-translations";
import { cn } from "@/lib/utils";

/**
 * Renders a translated string that marks one phrase for the gradient highlight, e.g.
 * "…a network that <accent>compounds.</accent>".
 *
 * The highlight has to be able to sit anywhere in the sentence, because word order moves
 * when the sentence is translated: German pushes the verb to the end, Spanish puts the
 * adjective after the noun. Two keys, a first half and an accent word, would hand every
 * translator two fragments and produce word order no native speaker would write.
 * A locale that drops the tag renders as plain text rather than breaking.
 */
export function withAccent(text: string) {
  return text.split(/<accent>|<\/accent>/).map((part, i) =>
    i % 2 === 1 ? (
      <span key={i} className="gradient-primary bg-clip-text text-transparent">
        {part}
      </span>
    ) : (
      part
    ),
  );
}

export function SiteHero({
  eyebrow,
  title,
  lede,
  primaryHref = "/auth/signup",
  primaryLabel,
  secondaryHref,
  secondaryLabel,
  aside,
  note,
}: {
  eyebrow: string;
  title: string;
  lede: string;
  primaryHref?: string;
  primaryLabel: string;
  secondaryHref?: string;
  secondaryLabel?: string;
  aside?: ReactNode;
  note?: string;
}) {
  return (
    <section className="relative overflow-hidden">
      {/* A painted wash rather than a blurred circle. A filtered element here left a hard
          edged disc behind in Safari on the way back up the page. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[420px]"
        style={{
          background:
            "radial-gradient(60% 100% at 30% 0%, rgba(33,122,246,0.13) 0%, rgba(33,122,246,0.05) 45%, rgba(250,250,250,0) 78%)",
        }}
      />
      <div className={cn("gs-page relative py-14 md:py-20", Boolean(aside) && "lg:py-24")}>
        <div className={cn(aside ? "grid items-center gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.92fr)] lg:gap-16" : "")}>
          <div className={cn("min-w-0", !aside && "max-w-[62rem]")}>
            <p className="gs-eyebrow text-primary">{eyebrow}</p>
            <h1 className="gs-display mt-4 text-balance text-neutral-900">{withAccent(title)}</h1>
            <p className="gs-lede mt-5 gs-measure text-neutral-600">{lede}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link href={primaryHref} className={buttonClasses({ size: "lg", className: "group" })}>
                {primaryLabel}
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" aria-hidden />
              </Link>
              {secondaryHref && secondaryLabel && (
                <Link href={secondaryHref} className={buttonClasses({ variant: "ghost", size: "lg" })}>
                  {secondaryLabel}
                </Link>
              )}
            </div>
            {note && <p className="mt-4 text-sm text-neutral-500">{note}</p>}
          </div>
          {aside && <div className="min-w-0">{aside}</div>}
        </div>
      </div>
    </section>
  );
}

/** A section heading used inside a page. Left aligned by default: the page picks one axis. */
export function SectionHead({
  eyebrow,
  title,
  body,
  align = "left",
  tone = "light",
}: {
  eyebrow?: string;
  title: string;
  body?: string;
  align?: "left" | "center";
  tone?: "light" | "dark";
}) {
  return (
    <div className={cn("min-w-0", align === "center" && "mx-auto max-w-3xl text-center")}>
      {eyebrow && <p className={cn("gs-eyebrow", tone === "dark" ? "text-white/60" : "text-primary")}>{eyebrow}</p>}
      <h2 className={cn("gs-headline mt-3 text-balance", tone === "dark" ? "text-white" : "text-neutral-900")}>
        {withAccent(title)}
      </h2>
      {body && (
        <p
          className={cn(
            "gs-lede mt-4 text-pretty",
            align === "center" ? "mx-auto max-w-2xl" : "gs-measure",
            tone === "dark" ? "text-white/70" : "text-neutral-600",
          )}
        >
          {body}
        </p>
      )}
    </div>
  );
}

/**
 * The outcome ladder: capability, then what it changes, then what that is worth.
 * Rendered as one continuous line with the rungs hung off it rather than three equal
 * boxes, because three equal boxes read as filler and hide the fact that this is a
 * sequence with a direction.
 */
export function Ladder({ steps }: { steps: { title: string; body: string }[] }) {
  return (
    <ol className="relative grid gap-8 md:grid-cols-3 md:gap-6" role="list">
      {/* The line the rungs hang off. It was `hidden` with no `md:block` to turn it back on,
          so it never drew at any width. */}
      <span
        aria-hidden
        className="pointer-events-none absolute left-[7px] top-2 h-[calc(100%-1rem)] w-px bg-gradient-to-b from-primary/60 via-primary/25 to-transparent md:left-0 md:top-[7px] md:h-px md:w-full md:bg-gradient-to-r"
      />
      {steps.map((step, index) => (
        <li key={step.title} className="relative min-w-0 pl-7 md:pl-0 md:pt-7">
          {/* bg-primary, NOT the gradient-primary utility. globals.css gives any span carrying
              that class `position: relative` from an unlayered rule, which beats Tailwind's
              `absolute` regardless of specificity, and adds a shine sweep. On a 15px dot that
              produced an inline white bar instead of a dot on the line. */}
          <span
            aria-hidden
            className={cn(
              "absolute left-0 top-1.5 h-[15px] w-[15px] rounded-full border-[3px] border-white md:top-0",
              index === 0 ? "bg-primary" : "bg-primary/35",
            )}
            style={{ boxShadow: "0 0 0 1px rgba(11,99,224,0.25)" }}
          />
          <h3 className="text-[17px] font-bold leading-snug tracking-[-0.015em] text-neutral-900">{step.title}</h3>
          <p className="mt-2 text-[15px] leading-relaxed text-neutral-600">{step.body}</p>
        </li>
      ))}
    </ol>
  );
}

/** A checked list of concrete, verifiable capabilities. Facts, not adjectives. */
export function CheckList({ items, className }: { items: string[]; className?: string }) {
  return (
    <ul className={cn("space-y-3", className)}>
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Check className="h-3 w-3" strokeWidth={3} aria-hidden />
          </span>
          <span className="text-[15px] leading-relaxed text-neutral-700">{item}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * A full bleed photograph with the argument sitting on it. The site's one recurring
 * loud move: it breaks a run of white sections and gives the page a horizon.
 * The gradient is painted on top rather than baked into the image so the text keeps
 * its contrast ratio whichever crop the browser picks.
 */
export function FullBleedPhoto({
  src,
  srcSmall,
  alt,
  eyebrow,
  title,
  body,
}: {
  src: string;
  srcSmall?: string;
  alt: string;
  eyebrow: string;
  title: string;
  body: string;
}) {
  return (
    <section className="relative overflow-hidden">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={src}
        {...(srcSmall ? { srcSet: `${srcSmall} 1400w, ${src} 2048w`, sizes: "100vw" } : {})}
        alt={alt}
        loading="lazy"
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-[#090f39]/95 via-[#090f39]/70 to-[#090f39]/25" />
      <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-[#090f39]/85 via-transparent to-transparent md:hidden" />
      <div className="gs-page relative flex min-h-[26rem] flex-col justify-end py-16 md:min-h-[34rem] md:py-24">
        <div className="max-w-xl">
          <p className="gs-eyebrow text-white/60">{eyebrow}</p>
          <h2 className="gs-headline mt-3 text-balance text-white">{title}</h2>
          <p className="mt-4 text-pretty text-[15px] leading-relaxed text-white/75 md:text-base">{body}</p>
        </div>
      </div>
    </section>
  );
}

/**
 * The frame every product mock sits in. One shadow, one radius, one border, defined once,
 * so five different mocks on five different pages still look like the same product.
 */
export function MockFrame({ children, className, label }: { children: ReactNode; className?: string; label?: string }) {
  return (
    <div className={cn("relative", className)}>
      <div className="relative overflow-hidden rounded-[1.75rem] border border-neutral-200/80 bg-white p-4 shadow-[0_24px_70px_-28px_rgba(15,23,42,0.35)] sm:p-5">
        {children}
      </div>
      {label && <p className="mt-3 text-center text-xs text-neutral-600">{label}</p>}
    </div>
  );
}

/** A text-and-evidence row. `flip` puts the evidence on the left, so a run of them alternates. */
export function SplitRow({
  eyebrow,
  title,
  body,
  items,
  aside,
  flip,
}: {
  eyebrow?: string;
  title: string;
  body: string;
  items?: string[];
  aside: ReactNode;
  flip?: boolean;
}) {
  return (
    <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
      <div className={cn("min-w-0", flip && "lg:order-2")}>
        <SectionHead eyebrow={eyebrow} title={title} body={body} />
        {items && <CheckList items={items} className="mt-7" />}
      </div>
      <div className={cn("min-w-0", flip && "lg:order-1")}>{aside}</div>
    </div>
  );
}

/**
 * FAQ. Native <details> so it opens with no JavaScript, works when hydration is slow,
 * and is keyboard operable for free. The FAQPage schema is emitted alongside it because
 * these are the exact questions answer engines are asked about GrowthSync.
 */
export function FaqList({ items, heading }: { items: { q: string; a: string }[]; heading?: string }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  return (
    <div className="min-w-0">
      {heading && <h2 className="gs-title text-neutral-900">{heading}</h2>}
      <div className={cn("divide-y divide-neutral-200 border-t border-neutral-200", heading && "mt-6")}>
        {items.map((item) => (
          <details key={item.q} className="group py-1">
            <summary className="flex cursor-pointer list-none items-start justify-between gap-6 py-4 text-left text-[16px] font-semibold leading-snug text-neutral-900 transition-colors hover:text-primary [&::-webkit-details-marker]:hidden">
              {item.q}
              <ChevronDown
                className="mt-0.5 h-5 w-5 shrink-0 text-neutral-400 transition-transform duration-200 group-open:rotate-180"
                aria-hidden
              />
            </summary>
            <p className="gs-measure pb-5 text-[15px] leading-relaxed text-neutral-600">{item.a}</p>
          </details>
        ))}
      </div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
    </div>
  );
}

/** The closing call to action. One per page, always last, always the same shape. */
export function CtaBand({ heading, body, primaryLabel }: { heading?: string; body?: string; primaryLabel?: string }) {
  const { t } = useTranslations("site");

  return (
    <section className="pb-20 pt-4 md:pb-28">
      <div className="gs-page">
        <div className="relative overflow-hidden rounded-[2rem] gradient-primary px-6 py-14 text-center shadow-[0_30px_80px_-24px_rgba(11,99,224,0.6)] md:px-12 md:py-16">
          <Shine duration="4s" className="via-white/20" />
          <div aria-hidden className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-white/[0.03] blur-2xl" />
          <div aria-hidden className="pointer-events-none absolute -bottom-20 -left-10 h-64 w-64 rounded-full bg-white/[0.03] blur-2xl" />
          <div className="relative">
            <h2 className="gs-headline mx-auto max-w-[26ch] text-balance text-white">{heading ?? t("cta.heading")}</h2>
            <p className="mx-auto mt-4 max-w-xl text-pretty text-base leading-relaxed text-white md:text-lg">
              {body ?? t("cta.body")}
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                href="/auth/signup"
                className="group inline-flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-white px-8 text-lg font-bold text-primary shadow-lg transition-all hover:scale-[1.02] hover:shadow-xl sm:w-auto"
              >
                {primaryLabel ?? t("cta.primary")}
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" aria-hidden />
              </Link>
              <Link
                href="/pricing"
                className="inline-flex min-h-11 items-center text-sm font-semibold !text-white underline-offset-4 hover:underline"
              >
                {t("cta.secondary")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/** Cross links so no page is a dead end. */
export function RelatedLinks({ links }: { links: { href: string; title: string; body: string }[] }) {
  const { t } = useTranslations("site");

  return (
    <section className="border-t border-neutral-200 py-14 md:py-16">
      <div className="gs-page">
        <p className="gs-eyebrow text-neutral-500">{t("common.relatedHeading")}</p>
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {links.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className="group flex min-w-0 items-start justify-between gap-4 rounded-2xl border border-neutral-200 bg-white px-5 py-4 transition-all hover:border-primary/40 hover:shadow-[0_12px_30px_-18px_rgba(11,99,224,0.5)]"
            >
              <span className="min-w-0">
                <span className="block text-[15px] font-bold text-neutral-900">{link.title}</span>
                <span className="mt-1 block text-[13.5px] leading-snug text-neutral-500">{link.body}</span>
              </span>
              <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-neutral-300 transition-all group-hover:translate-x-0.5 group-hover:text-primary" aria-hidden />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
